import { useState, useEffect, useCallback } from 'react';

export interface EpisodeQuizProgress {
  doneCount: number;
  totalQuizzes: number;
  isComplete: boolean;
  completionPercentage: number;
  quizzes: Record<number, boolean>; // quiz sequence -> completed
  accuracy?: Record<number, number>; // quiz sequence -> accuracy percentage
}

export interface EpisodeProgressResponse {
  exists: boolean;
  episodeId?: string;
  progress?: EpisodeQuizProgress;
  episodes?: Record<string, EpisodeQuizProgress>;
  meta?: {
    updated_at?: string;
    totalEpisodes?: number;
    completedEpisodes?: number;
  };
}

export function useEpisodeProgress(userId?: string, episodeId?: string) {
  const [data, setData] = useState<EpisodeProgressResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProgress = useCallback(async () => {
    if (!userId) {
      setData(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({ userId });
      if (episodeId) {
        params.append('episodeId', episodeId);
      }

      const response = await fetch(`/api/episodes/progress?${params}`);

      if (!response.ok) {
        throw new Error('Failed to fetch episode progress');
      }

      const result = await response.json();
      console.log('result', result);
      setData(result);
    } catch (err) {
      console.error('Failed to load episode progress:', err);
      setError(err instanceof Error ? err.message : 'Failed to load progress');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [userId, episodeId]);

  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  useEffect(() => {
    // 监听 shadowing 记录保存事件
    const handleRecordSaved = (event: CustomEvent) => {
      const detail = event.detail;
      // 如果保存的是当前 episode 的 quiz，刷新数据
      if (detail?.episodeId?.includes(':quiz')) {
        const [savedEpisodeId] = detail.episodeId.split(':');
        if (!episodeId || savedEpisodeId === episodeId) {
          setTimeout(fetchProgress, 500); // 延迟刷新确保数据已更新
        }
      }
    };

    window.addEventListener('shadowingRecordSaved', handleRecordSaved as EventListener);

    return () => {
      window.removeEventListener('shadowingRecordSaved', handleRecordSaved as EventListener);
    };
  }, [fetchProgress, episodeId]);

  // 获取特定 quiz 的完成状态
  const isQuizCompleted = useCallback((sequence: number): boolean => {
    if (!data?.progress?.quizzes) return false;
    return data.progress.quizzes[sequence] === true;
  }, [data?.progress?.quizzes]);

  // 获取特定 episode 的完成状态
  const isEpisodeCompleted = useCallback((targetEpisodeId?: string): boolean => {
    if (targetEpisodeId) {
      return data?.episodes?.[targetEpisodeId]?.isComplete === true;
    }
    return data?.progress?.isComplete === true;
  }, [data]);

  return {
    data,
    loading,
    error,
    refresh: fetchProgress,
    isQuizCompleted,
    isEpisodeCompleted,
  };
}